/**
 * The review's timeline — Step 54.
 *
 * Entries, not prose. The first version pre-filled a rich-text box from the
 * whole audit log and Kai's came out seventy lines long, most of it group
 * reassignments: *"the timeline should be actual timeline entries … which is
 * crap."*
 *
 * So this is a list of `{ time, what happened }` rows the author owns —
 * addable, editable, deletable, and sorted by time on save. It matches what
 * the reference exports actually contain ("10:08:03 — First monitoring issues
 * detected a loss of D1 Service"): a narrative somebody wrote, not a diff.
 *
 * Suggestions sit behind a button rather than being poured in. Six milestones
 * and the replies people actually sent are a useful starting point; deciding
 * they belong in the document is the author's call, and a box that fills
 * itself is one nobody reads before publishing.
 */
import { Plus, Trash2, Wand2 } from 'lucide-react';
import type { TimelineEntry } from '@itsm/shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

/** `datetime-local` wants local wall-clock, not an ISO instant. */
const toLocal = (ms: number) => {
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return d.toISOString().slice(0, 16);
};

export function TimelineEditor({
  entries,
  onChange,
  suggestions,
  readOnly,
}: {
  entries: TimelineEntry[];
  onChange: (next: TimelineEntry[]) => void;
  suggestions: TimelineEntry[];
  readOnly?: boolean;
}) {
  // Compared on time AND text: the same sentence can legitimately appear
  // twice at different moments ("Arqiva called back"), and two suggestions
  // can share a second.
  const key = (e: TimelineEntry) => `${e.at}|${e.text}`;
  const have = new Set(entries.map(key));
  const unused = suggestions.filter((s) => !have.has(key(s)));

  const patch = (i: number, next: Partial<TimelineEntry>) =>
    onChange(entries.map((e, x) => (x === i ? { ...e, ...next } : e)));

  return (
    <div className="space-y-2">
      {entries.length === 0 && (
        <p className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
          No entries yet.
        </p>
      )}
      {entries.map((e, i) => (
        <div key={i} className="flex items-start gap-2">
          <Input
            type="datetime-local"
            value={toLocal(e.at)}
            disabled={readOnly}
            onChange={(ev) => {
              const ms = new Date(ev.target.value).getTime();
              // An unparseable half-typed date must not wipe the row's time.
              if (!Number.isNaN(ms)) patch(i, { at: ms });
            }}
            className="w-52 shrink-0 dark:[color-scheme:dark]"
          />
          <Input
            value={e.text}
            disabled={readOnly}
            placeholder="What happened"
            onChange={(ev) => patch(i, { text: ev.target.value })}
          />
          {!readOnly && (
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              aria-label="Remove entry"
              onClick={() => onChange(entries.filter((_, x) => x !== i))}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ))}

      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              onChange([
                ...entries,
                // Now, because an entry added during an incident is almost
                // always about the minute you are in.
                { at: Date.now(), text: '' },
              ])
            }
          >
            <Plus className="h-4 w-4" /> Add entry
          </Button>
          {unused.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onChange([...entries, ...unused].sort((a, b) => a.at - b.at))}
            >
              <Wand2 className="h-4 w-4" /> Add {unused.length} from the record
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
